import { Chip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { diagnosticsApi, type DxProcedureDto, type DxProcedureDetailDto } from "@/api/diagnostics";
import { servicesApi } from "@/api/endpoints";
import { MasterListPage } from "./MasterListPage";
import { MasterFormPage } from "./MasterFormPage";
import { statusColumn, TextFieldCell, SelectCell, SwitchCell } from "./fields";

const BASE = "/diagnostics/radiology/procedures";
const LIST_KEY = ["dx-procedures"];

export function RadiologyProceduresPage() {
  return (
    <MasterListPage<DxProcedureDto>
      breadcrumbs={["Diagnostics", "Radiology"]}
      title="Procedures"
      subtitle="Imaging procedures per modality."
      queryKey={LIST_KEY}
      listFn={(inc) => diagnosticsApi.listProcedures(undefined, inc)}
      deleteFn={diagnosticsApi.deleteProcedure}
      basePath={BASE}
      newLabel="New procedure"
      labelOf={(r) => r.name}
      deleteNote="It will be refused if it has recorded studies or has ever been ordered."
      columns={[
        { field: "code", headerName: "Code", width: 130 },
        { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
        { field: "modalityName", headerName: "Modality", width: 140 },
        {
          field: "requiresContrast", headerName: "Contrast", width: 110,
          renderCell: (p) => p.row.requiresContrast
            ? <Chip size="small" label="Yes" color="info" variant="outlined" />
            : <>—</>,
        },
        {
          field: "estimatedMinutes", headerName: "Est. mins", width: 100, align: "right", headerAlign: "right",
          valueFormatter: (v: number | null | undefined) => v ?? "—",
        },
        statusColumn<DxProcedureDto>(),
      ]}
    />
  );
}

interface Values {
  modalityId: string; serviceId: string; code: string; name: string;
  requiresContrast: boolean; estimatedMinutes: string; preparationInstructions: string; isActive: boolean;
}

export function ProcedureFormPage() {
  const modalities = useQuery({ queryKey: ["dx-modalities"], queryFn: () => diagnosticsApi.listModalities() });
  const services = useQuery({ queryKey: ["services-all"], queryFn: () => servicesApi.list({ pageSize: 200 }) });

  return (
    <MasterFormPage<Values, DxProcedureDetailDto>
      breadcrumbs={["Diagnostics", "Radiology", "Procedures"]}
      noun="procedure"
      basePath={BASE}
      listQueryKey={LIST_KEY}
      detailQueryKey={(id) => ["dx-procedure", id]}
      defaultValues={{
        modalityId: "", serviceId: "", code: "", name: "",
        requiresContrast: false, estimatedMinutes: "", preparationInstructions: "", isActive: true,
      }}
      getFn={diagnosticsApi.getProcedure}
      toValues={(d) => ({
        modalityId: d.modalityId, serviceId: d.serviceId ?? "", code: d.code, name: d.name,
        requiresContrast: d.requiresContrast,
        estimatedMinutes: d.estimatedMinutes == null ? "" : String(d.estimatedMinutes),
        preparationInstructions: d.preparationInstructions ?? "", isActive: d.isActive,
      })}
      createFn={(v) => diagnosticsApi.createProcedure({
        modalityId: v.modalityId, code: v.code.trim(), name: v.name.trim(),
        requiresContrast: v.requiresContrast, serviceId: v.serviceId || null,
        estimatedMinutes: v.estimatedMinutes === "" ? null : Number(v.estimatedMinutes),
        preparationInstructions: v.preparationInstructions.trim() || null,
      })}
      updateFn={(id, v) => diagnosticsApi.updateProcedure(id, {
        modalityId: v.modalityId, code: v.code.trim(), name: v.name.trim(),
        requiresContrast: v.requiresContrast, serviceId: v.serviceId || null,
        estimatedMinutes: v.estimatedMinutes === "" ? null : Number(v.estimatedMinutes),
        preparationInstructions: v.preparationInstructions.trim() || null, isActive: v.isActive,
      })}
    >
      {({ control }, isEdit) => (
        <>
          <SelectCell control={control} name="modalityId" label="Modality" span={5} required
            options={(modalities.data ?? []).map(m => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
            emptyLabel="— select modality —" />
          <SelectCell control={control} name="serviceId" label="Billing service (price/GST)" span={7}
            options={(services.data?.items ?? []).map(s => ({ value: s.id, label: `${s.code} — ${s.name}` }))} />
          <TextFieldCell control={control} name="code" label="Code" span={3} required />
          <TextFieldCell control={control} name="name" label="Name" span={6} required />
          <TextFieldCell control={control} name="estimatedMinutes" label="Est. minutes" span={3} type="number" />
          <TextFieldCell control={control} name="preparationInstructions" label="Preparation instructions"
            span={12} multiline minRows={2} placeholder="Fasting, full bladder, contrast consent…" />
          <SwitchCell control={control} name="requiresContrast" label="Requires contrast" />
          {isEdit && <SwitchCell control={control} name="isActive" label="Active" />}
        </>
      )}
    </MasterFormPage>
  );
}
